import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnsType } from 'antd/es/table';
import { CircleArrowDown, CircleArrowUp } from 'lucide-react';
import { TableAction } from '@/components';
import LedgerFilterBar from '@/components/AdvanceCod/LedgerFilterBar';
import { SignedAmount } from '@/components/AdvanceCod';
import { useFetchAdvanceLedger } from '@/hooksApi';
import { formatDateByDayjs } from '@/utils';

const PAGE_SIZE = 25;

export interface AdvanceLedgerRow {
  key: string;
  platform_code: string;
  created_at: string;
  subject: string;
  reference: string | null;
  amount: number;
  status: string | null;
}

// Same 3-state pill as finance list's TransactionPillBadge
const transactionStatusStyles: Record<string, { color: string; background: string }> = {
  enabled: { color: 'rgb(59,130,246)', background: 'rgba(59,130,246,0.12)' },
  pending: { color: 'rgb(187,120,27)', background: 'rgba(245,158,11,0.12)' },
  paid: { color: 'rgb(39,155,101)', background: 'rgba(49,196,127,0.12)' },
};

/**
 * Ledger columns — EXACT clone of the Remittance/Transactions house table
 * row structure (finance/list.tsx): icon + code + date stacked cell, pill
 * status, signed amount. Dates go through `formatDateByDayjs` so AR renders
 * Arabic-Indic digits like every other list (QA defect 10); signed money is
 * LTR-isolated via SignedAmount (QA defect 6).
 */
const getLedgerColumns = (t: (key: string) => string): ColumnsType<AdvanceLedgerRow> => [
  {
    title: t('transaction'),
    key: 'transaction',
    render: (_: unknown, record) => {
      const isCredit = record.amount >= 0;
      return (
        <div className='flex items-center gap-2.5'>
          <div
            className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full'
            style={{ background: isCredit ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)' }}
          >
            {isCredit ? (
              <CircleArrowDown className='h-4 w-4' style={{ color: 'rgb(34,197,94)' }} />
            ) : (
              <CircleArrowUp className='h-4 w-4' style={{ color: 'rgb(239,68,68)' }} />
            )}
          </div>
          <div className='flex flex-col gap-0.5'>
            <span className='text-[13px] font-bold text-[rgb(57,57,60)]'>
              <bdi>{record.platform_code}</bdi>
            </span>
            <span className='text-[11px] text-[rgb(115,123,140)]'>
              <bdi>{formatDateByDayjs(record.created_at, 'D MMMM YYYY')}</bdi>
            </span>
          </div>
        </div>
      );
    },
  },
  {
    // batch-level entries show the batch platform code (BXAB-…), per-order
    // entries the order code — same cell, different id namespace (DESIGN §5)
    title: t('advance_cod_reference'),
    key: 'reference',
    render: (_: unknown, record) => (
      <span className='text-[13px] font-medium text-[rgb(57,57,60)]'>
        <bdi>{record.reference || '—'}</bdi>
      </span>
    ),
  },
  {
    title: t('title'),
    key: 'title',
    render: (_: unknown, record) => (
      <span className='block max-w-[220px] truncate text-[13px] text-[rgb(57,57,60)]'>
        {t(`transaction_type_${record.subject}`)}
      </span>
    ),
  },
  {
    title: t('amount'),
    key: 'amount',
    render: (_: unknown, record) => (
      <SignedAmount
        amount={record.amount}
        className='whitespace-nowrap text-[13px] font-bold'
        style={{ color: record.amount >= 0 ? 'rgb(34,197,94)' : 'rgb(239,68,68)' }}
      />
    ),
  },
  {
    title: t('status'),
    key: 'status',
    render: (_: unknown, record) => {
      const styles = transactionStatusStyles[record.status ?? ''] ?? {
        color: 'rgb(115,123,140)',
        background: 'rgba(115,123,140,0.12)',
      };
      return (
        <span
          className='inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-semibold'
          style={{ color: styles.color, background: styles.background }}
        >
          {t(`transaction_status.${record.status ?? ''}`)}
        </span>
      );
    },
  },
];

/**
 * Ledger tab — clone of the Remittance page pattern (session-12 rework):
 * filter buttons + house TableAction + server-side pagination. Data arrives
 * server-filtered to the four ffc_* subjects (no client filtering, no
 * load-more, no sparkline, no client-computed running balance).
 */
function LedgerTab() {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [subjectFilter, setSubjectFilter] = useState<string | undefined>();
  const [dateFrom, setDateFrom] = useState<string | undefined>();
  const [dateTo, setDateTo] = useState<string | undefined>();

  const { data, isFetching, isLoading } = useFetchAdvanceLedger(currentPage, PAGE_SIZE, {
    filter_subject: subjectFilter,
    filter_created_at_from: dateFrom,
    filter_created_at_to: dateTo,
  });

  const rows: AdvanceLedgerRow[] = (data?.object?.items ?? []).map((item) => ({
    key: item.uid,
    platform_code: item.platform_code ?? '—',
    created_at: item.created_at,
    subject: item.subject,
    reference: item.order_platform_code ?? item.item_platform_code ?? null,
    amount: Number(item.amount ?? 0),
    status: item.status?.slug ?? null,
  }));

  const hasActiveFilter = !!subjectFilter || !!dateFrom || !!dateTo;

  if (!isLoading && !isFetching && rows.length === 0 && !hasActiveFilter && currentPage === 1) {
    return (
      <div
        className='rounded-2xl border border-dashed border-[rgb(200,200,196)] bg-white px-6 py-12 text-center'
        data-testid='advance-cod-no-ledger'
      >
        <span className='text-[13px] text-[rgb(115,123,140)]'>
          {String(t('advance_cod_no_transactions'))}
        </span>
      </div>
    );
  }

  return (
    <div className='flex flex-col'>
      <LedgerFilterBar
        subject={subjectFilter}
        onSubjectChange={(subject) => {
          setSubjectFilter(subject);
          setCurrentPage(1);
        }}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateChange={(from, to) => {
          setDateFrom(from);
          setDateTo(to);
          setCurrentPage(1);
        }}
      />
      <TableAction
        columns={getLedgerColumns(t as (key: string) => string)}
        data={rows}
        pageSize={PAGE_SIZE}
        current={currentPage}
        total={data?.object?.total ?? 0}
        loading={isFetching}
        rowSelectionEnabled={false}
        onPaginationChange={(page) => setCurrentPage(page)}
      />
    </div>
  );
}

export { getLedgerColumns };
export default LedgerTab;
